/**
 * CUTTING WASTE, ON THE AREA THE CUSTOMER IS CHARGED FOR.
 *
 * Jacky, rebuilding the proposal: *"area SQM need also wastage - use rule is < 50 SQM 7%, over 50 SQM 5%,
 * customer will see we will charge in the end by actual. (that wastage was for straight plank) Herringbon
 * 20%, Chevron 25% same % for all sizes."*
 *
 * The percentages are NOT in this file. `artemis_services.waste` (`packages/config/src/artemis-services.ts`)
 * is the document he edits, and a caller hands that object straight in — the same rule the service rates
 * already follow. What lives here is the READING of his sentence, in one place, so the proposal, the CRM
 * and EmmA cannot each decide for themselves what "50 SQM" means:
 *
 *   · the threshold is an under/over pair over the same figure, and **exactly 50 m² takes the 5% tier** —
 *     a floor of exactly 50 is not "under 50".
 *   · herringbone and chevron are ONE percentage "for all sizes" — the threshold never touches them.
 *   · the customer is told before the work starts, in their own language: a screen prints the id from
 *     `wasteNoticeId` with `{percent}` from `wastePercent(wasteFor(...))`, and the English it stands for is
 *     the `note` returned below.
 *
 * What comes back is a QUANTITY, never money. The engine prices the measured area and its figure is not
 * touched; this is the area the customer is shown they are charged for, which is his rule.
 */
const round2 = (n) => Math.round(n * 100) / 100;
/**
 * The fraction to add for the lay — 0.07, not 7.
 *
 * A line that measures nothing is charged no allowance whatever the lay: zero, absent and not-a-number
 * all return 0 rather than inventing a percentage on an area nobody measured.
 */
export function wasteFor(areaM2, pattern, rules) {
    if (!(areaM2 > 0))
        return 0;
    if (pattern === 'herringbone')
        return rules.herringbone;
    if (pattern === 'chevron')
        return rules.chevron;
    // His words: "< 50 SQM 7%, over 50 SQM 5%". Exactly 50 is the 5% tier.
    return areaM2 < rules.thresholdM2 ? rules.straightUnder50 : rules.straightOver50;
}
/** The percentage as a customer reads it: 7, not 0.07 — and not 0.07 × 100, which is 7.000000000000001. */
export function wastePercent(fraction) {
    return Math.round(fraction * 10000) / 100;
}
export function wasteNoticeId(pattern) {
    return pattern === 'straight' ? 'priced.waste.straight' : 'priced.waste.pattern';
}
/** The English source those two ids translate. Kept private: `chargeableArea` is what carries it out. */
const noteFor = (percent, pattern) => (pattern === 'straight'
    ? `Includes ${percent}% for cutting and waste; the final charge is by actual measurement.`
    : `Includes ${percent}% for cutting and waste on a patterned lay; the final charge is by actual measurement.`);
/**
 * The measured area, the allowance, and the area the customer is charged for.
 *
 * The measured area is rounded to the two decimals the customer reads BEFORE the allowance is taken, so
 * the figures on the page add up (43.26 m² + 7% is 46.29 m²) and the threshold is decided on the number
 * that was actually shown.
 */
export function chargeableArea(areaM2, pattern, rules) {
    const measuredM2 = round2(Number.isFinite(areaM2) && areaM2 > 0 ? areaM2 : 0);
    const waste = wasteFor(measuredM2, pattern, rules);
    return {
        measuredM2,
        waste,
        chargeableM2: round2(measuredM2 * (1 + waste)),
        // Nothing measured, nothing to tell them.
        note: measuredM2 > 0 ? noteFor(wastePercent(waste), pattern) : '',
    };
}
/**
 * A catalogue product's `parquet_form` as a lay pattern.
 *
 * The live values in `public.products` (read 2026-09-19, 228 products not deleted) are exactly: `plank`
 * (104), `herringbone` (13), `chevron` (4) and NULL (107). So half the catalogue states no form at all,
 * and straight is the lay that assumes nothing — a form we do not recognise must not be quietly charged a
 * patterned rate, which is the expensive direction to be wrong in.
 */
export function layPatternOf(parquetForm) {
    const value = (parquetForm ?? '').trim().toLowerCase();
    if (value.includes('herringbone'))
        return 'herringbone';
    if (value.includes('chevron'))
        return 'chevron';
    return 'straight';
}
//# sourceMappingURL=waste.js.map